import React from 'react'
import { Input, Tag, Tooltip } from 'antd'
import { FilterFilled } from '@ant-design/icons'
import { colours } from '@qflow/theme'
import {
  RecordLink,
  RecordsSelect
} from 'portal/components/RecordsTable/RecordsColumn'
import { icons } from 'portal/lib/primitives'
import { RECORD_STATUS } from 'portal/lib/constants'
import { recordsComponentFactory } from './recordsComponentFactory'

const { Search } = Input
const MAX_TAG_DISPLAY_LENGTH = 18
const TAG_TRUNCATE_LENGTH = 15

const getDisplayTagValue = tag =>
  tag.length > MAX_TAG_DISPLAY_LENGTH
    ? `${tag.slice(0, TAG_TRUNCATE_LENGTH)}...`
    : tag

function setCellId(record) {
  if (record.removed) {
    return {
      id: 'RecordsTableRow',
      style: {
        textDecoration: 'line-through',
        opacity: '0.5'
      }
    }
  }
  return {
    id: 'RecordsTableCell'
  }
}

export const DeliveryConfig = {
  ModuleName: 'Delivery',
  dateField: 'deliveryDate',
  buildRecordsForDisplay: function (records, projectTimeZone) {
    return {
      data: (records ?? []).map(record => {
        const productGroupNames = Array.from(
          record.data.products?.reduce((pg, item) => {
            if (!item.isDeleted && !item.isRemoved && item.productGroupName) {
              pg.add(item.productGroupName)
            }

            return pg
          }, new Set()) || []
        ).join(',\n')

        return {
          record: record,
          createdAt: new Date(record.createdAt).toLocaleDateString([], {
            timeZone: projectTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }),
          date: record.data.deliveryArrivalDate
            ? new Date(record.data.deliveryArrivalDate).toLocaleString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : undefined,
          deliveryId: record.data.deliveryId,
          receiverReferenceNumber: record.data.receiverReferenceNumber,
          deliveryAddressKnown: record.data.deliveryAddressKnown,
          dispatchAddressKnown: record.data.dispatchAddressKnown,
          tradeContractor: record.data.tradeContractor,
          knownSupplierName: record.data.knownSupplierName,
          clientReference: record.data.clientReference,
          recordIdRaw: record.recordId,
          productGroupNames: productGroupNames,
          productsWithDesignMatchesCount: record.productsWithDesignMatchesCount,
          tags: record.tags || [],
          removed: record.status === RECORD_STATUS.REMOVED
        }
      })
    }
  },
  buildColumnsForTable: function (
    { size },
    state,
    fetchOptions,
    assignedToOptions,
    filters,
    updateFilters,
    projectId,
    tableId,
    accountId,
    sortById,
    updateSortById,
    isImperial,
    text,
    fields,
    projectTags
  ) {
    return [
      {
        dataIndex: 'createdAt',
        className: 'createdAt',
        title: 'Created At',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        dataIndex: 'date',
        className: 'arrivalDate',
        title: 'Arrival Date',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Delivery ID</div>,
        dataIndex: 'deliveryId',
        className: 'deliveryId',
        sorter: true,
        sortOrder: sortById,
        ellipsis: true,
        filterDropdown: () => (
          <Search
            defaultValue={filters?.deliveryId}
            allowClear
            placeholder={'Note: Case sensitive'}
            onSearch={value => {
              updateFilters('deliveryId', value)
            }}
            onBlur={e => {
              if (e.target.value !== filters?.deliveryId) {
                updateFilters('deliveryId', e.target.value)
              }
            }}
            data-testid={'deliveryIdFilter'}
          />
        ),
        filtered: !!filters?.deliveryId,
        filterIcon: filtered => {
          return (
            <Tooltip
              title={
                filters?.deliveryId
                  ? 'Filter option applied'
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        onCell: setCellId
      },
      {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Receiver Reference</div>,
        dataIndex: 'receiverReferenceNumber',
        className: 'receiverReferenceNumber',
        ellipsis: true,
        filterDropdown: () => (
          <Search
            defaultValue={filters?.receiverReferenceNumber}
            allowClear
            placeholder={'Note: Case sensitive'}
            onSearch={value => {
              updateFilters('receiverReferenceNumber', value, filters)
            }}
            onBlur={e => {
              if (e.target.value !== filters?.receiverReferenceNumber) {
                updateFilters('receiverReferenceNumber', e.target.value)
              }
            }}
            data-testid={'receiverReferenceNumberFilter'}
          />
        ),
        filtered: !!filters?.receiverReferenceNumber,
        filterIcon: filtered => {
          return (
            <Tooltip
              title={
                filters?.receiverReferenceNumber
                  ? 'Filter option applied'
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Delivery Address</div>,
        dataIndex: 'deliveryAddressKnown',
        className: 'deliveryAddressKnown',
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'deliveryAddressKnown',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters?.deliveryAddressKnown &&
          filters?.deliveryAddressKnown.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="deliveryAddressKnown"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.deliveryAddressKnown?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Dispatch Address</div>,
        dataIndex: 'dispatchAddressKnown',
        className: 'dispatchAddressKnown',
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'dispatchAddressKnown',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters?.dispatchAddressKnown &&
          filters?.dispatchAddressKnown.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="dispatchAddressKnown"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.dispatchAddressKnown?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Trade Contractor</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'tradeContractor',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters?.tradeContractor && filters?.tradeContractor.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="tradeContractor"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.tradeContractor?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        dataIndex: 'tradeContractor',
        className: 'tradeContractor',
        onCell: setCellId
      },
      size > 2 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Supplier Company</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'knownSupplierName',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters?.knownSupplierName && filters?.knownSupplierName.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="knownSupplierName"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.knownSupplierName?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        dataIndex: 'knownSupplierName',
        className: 'knownSupplierName',
        onCell: setCellId
      },
      {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>External Reference</div>,
        dataIndex: 'clientReference',
        className: 'clientReference',
        ellipsis: true,
        filterDropdown: () => (
          <Search
            defaultValue={filters?.clientReference}
            allowClear
            placeholder={'Note: Case sensitive'}
            onSearch={value => {
              updateFilters('clientReference', value, filters)
            }}
            onBlur={e => {
              if (e.target.value !== filters?.clientReference) {
                updateFilters('clientReference', e.target.value)
              }
            }}
            data-testid={'clientReferenceFilter'}
          />
        ),
        filtered: !!filters?.clientReference,
        filterIcon: filtered => {
          return (
            <Tooltip
              title={
                filters?.clientReference
                  ? 'Filter option applied'
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Product Groups</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'products[].productGroupName',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters?.['products[].productGroupName'] &&
          filters?.['products[].productGroupName'].length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="products[].productGroupName"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.['products[].productGroupName']?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        dataIndex: 'productGroupNames',
        className: 'productGroupNames',
        onCell: setCellId
      },
      size > 1 && {
        title: 'Assigned To',
        ellipsis: false,
        dataIndex: 'assignedTo',
        className: 'assignedTo',
        filterDropdown: () =>
          RecordsSelect(
            'assignedTo',
            state,
            assignedToOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered: filters?.assignedTo && filters?.assignedTo.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="assignedTo"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.assignedTo?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        width: '8rem',
        align: 'center'
      },
      {
        title: 'Tags',
        dataIndex: 'tags',
        className: 'tags',
        ellipsis: false,
        filterDropdown: () =>
          RecordsSelect(
            'tags',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId,
            projectTags || []
          ),
        filtered: filters?.tags && filters?.tags.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="tags"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        filterIcon: filtered => {
          const count = filters?.tags?.length || 0

          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        render: tags =>
          tags && tags.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
              {tags.map(t => (
                <Tooltip
                  key={t}
                  title={t.length > MAX_TAG_DISPLAY_LENGTH ? t : null}
                >
                  <Tag
                    color={colours.PALE_TEAL}
                    bordered={false}
                    style={{ margin: 0, color: colours.CORE_NAVY }}
                  >
                    {getDisplayTagValue(t)}
                  </Tag>
                </Tooltip>
              ))}
            </div>
          ) : null,
        onCell: setCellId
      },
      {
        dataIndex: 'productsWithDesignMatchesCount',
        className: 'productsWithDesignMatchesCount',
        title: (
          <Tooltip title="Number of products with design matches for this record">
            <span>
              <icons.DesignMatch alt="Design Matches" />
            </span>
          </Tooltip>
        ),
        width: '3rem',
        align: 'center',
        render: (num, record, index) => {
          if (num === 0) {
            return null
          }
          const className = record.hasMaterialWatchlistAlert
            ? ' record-alert-cell'
            : ''
          return (
            <Tooltip
              title={className ? 'Record has material watchlist alert' : ''}
            >
              <div
                className={`design-matches-count${className}`}
                data-testid="designMatchesCountCell"
              >
                {num}
              </div>
            </Tooltip>
          )
        }
      },
      RecordLink(setCellId, filters, updateFilters)
    ]
  },
  initialFilterState: {
    'deliveryId': '',
    'deliveryDate': { startDate: undefined, endDate: undefined },
    'receiverReferenceNumber': '',
    'deliveryAddressKnown': [],
    'dispatchAddressKnown': [],
    'tradeContractor': [],
    'knownSupplierName': [],
    'clientReference': '',
    'tags': [],
    'startDate': undefined,
    'endDate': undefined,
    'products[].productGroupName': []
  }
}

export const Delivery = recordsComponentFactory(DeliveryConfig)
